import React from 'react';

/**
 * SVG BuoyConditionsIcon component props
 * @property {number} [size=35] - The width and height of the icon in pixels.
 * @property {string} [color='white'] - The fill color of the buoy shape.
 * @property {string} [stroke='black'] - The color of the main buoy outline (stroke).
 * @property {number} [strokeWidth=2] - The width of the main buoy outline (stroke).
 * @property {string} [borderStroke] - The color of the border/outline around the main stroke. If not set or borderStrokeWidth is 0, no border is rendered.
 * @property {number} [borderStrokeWidth] - The width of the border/outline around the main stroke. If 0 or not set, no border is rendered.
 */
export interface BuoyConditionsIconProps {
  size?: number;
  color?: string;
  stroke?: string;
  strokeWidth?: number;
  borderStroke?: string;
  borderStrokeWidth?: number;
}

export const BuoyConditionsIcon: React.FC<BuoyConditionsIconProps> = ({
  size = 35,
  color = 'white',
  stroke = 'black',
  strokeWidth = 2,
  borderStroke = '',
  borderStrokeWidth = 0,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 512 512"
    fill={color}
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Buoy Conditions Icon"
  >
    {/* Border outline path, rendered first if borderStrokeWidth > 0 */}
    {borderStroke && borderStrokeWidth > 0 && (
      <path
        d="m332.53 21.45-38.774 56.273 14.422 2.027 6.93.975-1.948 13.863-31.687-4.455-1.256 8.912-4.45 31.652-.007.037-.004.037-7.654 54.427-31.03-4.363-1.79 8.14-40.817 185.604c6.63-.623 12.886-1.17 18.79-1.63l3.35-15.24 134.624 18.93-.984 15.587c5.568 2.076 11.38 4.294 17.61 6.732L380.33 200.98l-31.027-4.363 13.367-95.066-31.69-4.456 1.95-13.862 21.353 3.002L332.53 21.45zM174.007 71.224c-15.522 3.527-31.163 14.648-45.994 28.476-14.326-13.384-28.679-23.53-45.538-28.35l-4.95 17.304c15.107 4.32 28.602 14.202 44.112 29.71L128 124.73l6.363-6.367c15.35-15.35 32.643-27.09 43.63-29.588l-3.987-17.55zm142.03 42.213a23.58 23.58 0 0 1 3.526.23c12.685 1.784 21.358 13.294 19.575 25.978-1.784 12.685-13.292 21.356-25.977 19.573-12.684-1.785-21.36-13.295-19.576-25.98 1.616-11.495 11.224-19.694 22.45-19.803zm-27.17 53.355c5.884 5.357 13.364 9.068 21.786 10.252 8.424 1.184 16.637-.32 23.77-3.848l-2.943 20.912-45.552-6.405 2.94-20.912zm-17.95 36.978 70.7 9.943-38.762 20.06-31.938-30.002zm-21.457 4.544 36.442 34.23-49.617 25.68 13.174-59.91zm111.384 15.717-3.82 60.624-40.256-37.812 44.076-22.81zm-61.028 31.583 38.8 36.444-85.886-12.077 47.086-24.367zm-69.423 39.405 124.873 17.558-2.9 46.045-131.882-18.544 9.91-45.058zm21.962 93.798c-23.248-.01-51.884 3.056-95.332 7.653L95.807 494h320.386l-18.384-63.674c-72.987-29.56-100.19-40.694-140.874-41.467-1.505-.03-3.03-.043-4.58-.044z"
        fill="none"
        stroke={borderStroke}
        strokeWidth={borderStrokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    )}
    <g>
      <path
        d="m332.53 21.45-38.774 56.273 14.422 2.027 6.93.975-1.948 13.863-31.687-4.455-1.256 8.912-4.45 31.652-.007.037-.004.037-7.654 54.427-31.03-4.363-1.79 8.14-40.817 185.604c6.63-.623 12.886-1.17 18.79-1.63l3.35-15.24 134.624 18.93-.984 15.587c5.568 2.076 11.38 4.294 17.61 6.732L380.33 200.98l-31.027-4.363 13.367-95.066-31.69-4.456 1.95-13.862 21.353 3.002L332.53 21.45zM174.007 71.224c-15.522 3.527-31.163 14.648-45.994 28.476-14.326-13.384-28.679-23.53-45.538-28.35l-4.95 17.304c15.107 4.32 28.602 14.202 44.112 29.71L128 124.73l6.363-6.367c15.35-15.35 32.643-27.09 43.63-29.588l-3.987-17.55zm142.03 42.213a23.58 23.58 0 0 1 3.526.23c12.685 1.784 21.358 13.294 19.575 25.978-1.784 12.685-13.292 21.356-25.977 19.573-12.684-1.785-21.36-13.295-19.576-25.98 1.616-11.495 11.224-19.694 22.45-19.803zm-27.17 53.355c5.884 5.357 13.364 9.068 21.786 10.252 8.424 1.184 16.637-.32 23.77-3.848l-2.943 20.912-45.552-6.405 2.94-20.912zm-17.95 36.978 70.7 9.943-38.762 20.06-31.938-30.002zm-21.457 4.544 36.442 34.23-49.617 25.68 13.174-59.91zm111.384 15.717-3.82 60.624-40.256-37.812 44.076-22.81zm-61.028 31.583 38.8 36.444-85.886-12.077 47.086-24.367zm-69.423 39.405 124.873 17.558-2.9 46.045-131.882-18.544 9.91-45.058zm21.962 93.798c-23.248-.01-51.884 3.056-95.332 7.653L95.807 494h320.386l-18.384-63.674c-72.987-29.56-100.19-40.694-140.874-41.467-1.505-.03-3.03-.043-4.58-.044z"
        fill={color}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </g>
  </svg>
);
